// @flow

import React, { type Element, useEffect, useState } from 'react'
import { Typography, Grid, Box } from '@material-ui/core'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import PrivateFacilityCreationModal from './PrivateFacilityCreationModal'
import type { PrivateFacility as Facility } from '../types'
import grey from '@material-ui/core/colors/grey'
import FacilityItem from './FacilityItem'
import { green } from '../../Common/constants'
import PrivateFacilityDeleteModal from './PrivateFacilityDeleteModal'
import AlertSuccess from '../../Common/AlertSuccess'

type Props = {|
  handleServerErrors: ({ [string]: Array<string> }) => void,
  createdFacilities: Array<Facility>,
  setFacilityToAdd?: (Facility) => void,
  setFacilityToDelete?: (string) => void,
|}

function PrivateFacility({
  handleServerErrors,
  createdFacilities,
  setFacilityToAdd,
  setFacilityToDelete,
}: Props): Element<typeof React.Fragment> {
  const [privateFacilities, setPrivateFacilities] = useState(createdFacilities)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const closeCreateModal = () => setCreateModalOpen(false)
  const openCreateModal = () => setCreateModalOpen(true)

  const [successMessage, setSuccessMessage] = useState('')
  const showDeleteSuccess = () => {
    if (setFacilityToDelete) setFacilityToDelete(facilityToBeDeleted)
    setSuccessMessage('Private facility deleted successfully')
  }
  const showCreateSuccess = () => {
    setSuccessMessage('Private facility created successfully')
  }

  const [facilityToBeDeleted, setFacilityToBeDeleted] = useState('')
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const closeDeleteModal = () => setDeleteModalOpen(false)
  const openDeleteModal = () => setDeleteModalOpen(true)

  useEffect(() => {
    if (facilityToBeDeleted) {
      openDeleteModal()
    }
  }, [facilityToBeDeleted])

  useEffect(() => {
    if (setFacilityToAdd && privateFacilities.length > createdFacilities.length) {
      setFacilityToAdd(privateFacilities.slice(-1)[0])
    }
  }, [createdFacilities.length, privateFacilities, setFacilityToAdd])

  return (
    <Grid container>
      {successMessage && <AlertSuccess message={successMessage} setMessage={setSuccessMessage} />}
      <Grid item xs={12}>
        <Box bgcolor="#F1F3F3" borderRadius={8} p={2.5} mb={4}>
          <Grid container justify="space-between">
            <Box color={grey[900]} fontWeight="fontWeightMedium">
              <Typography component="span" variant="body2">
                My Facility
              </Typography>
            </Box>
            <Box ml="auto" display="flex" style={{ cursor: 'pointer' }} onClick={openCreateModal}>
              <AddCircleIcon style={{ color: green }} />
              <Box pl={0.5} color={green} fontWeight="fontWeightMedium" my="auto">
                <Typography component="span" variant="body2">
                  Add New
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Box pt={1.5} pb={2} color={grey[600]}>
            <Typography component="span">
              Create a facility for every office that’s registered with MSP for BCP / LFP. You’ll be
              able to select these facilities in a claim and also set a facility as your default
              facility.
            </Typography>
          </Box>
          {privateFacilities.length > 0 && (
            <Box borderTop={1} color={grey[300]}>
              {privateFacilities.map((facility) => (
                <FacilityItem
                  facility={facility}
                  deleteFacility={setFacilityToBeDeleted}
                  key={facility.code}
                />
              ))}
            </Box>
          )}
        </Box>
        <PrivateFacilityCreationModal
          handleServerErrors={handleServerErrors}
          modalOpen={createModalOpen}
          closeModal={closeCreateModal}
          createdFacilities={privateFacilities}
          setCreatedFacilities={setPrivateFacilities}
          showSuccess={showCreateSuccess}
        />
        <PrivateFacilityDeleteModal
          facilityId={facilityToBeDeleted}
          handleServerErrors={handleServerErrors}
          modalOpen={deleteModalOpen}
          closeModal={closeDeleteModal}
          createdFacilities={privateFacilities}
          setCreatedFacilities={setPrivateFacilities}
          showSuccess={showDeleteSuccess}
        />
      </Grid>
    </Grid>
  )
}

export default PrivateFacility
